import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { isEqual } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class SelectionService<T> {

  private destroyRef = inject(DestroyRef);

  private lastSelection = signal<T[]>([]);
  private selectedItems = signal<T[]>([]);
  public items = signal<T[]>([]);
  public hoveredItem = signal<T | null>(null);

  public selectionForm = new FormGroup({
    masterCheckbox: new FormControl<number>(0),
  });

  constructor() {
    this.masterCheckbox.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => {
      if (value === 2) {
        this.selectedItems.set(this.items());
        this.lastSelection.set(this.items());
      } else if (value === 0) {
        this.selectedItems.set([]);
        this.lastSelection.set([]);
      }
    });
  }

  public get masterCheckbox(): FormControl {
    return this.selectionForm.controls.masterCheckbox as FormControl;
  }

  public selectItem(row: T, event: MouseEvent): void {

    let newSelection: T[] = [];
    event.stopImmediatePropagation();
    event.preventDefault();

    const textSelection = window.getSelection()?.toString();
    if (textSelection?.trim().length && !event.shiftKey) return;

    window.getSelection()?.removeAllRanges();

    if (event.shiftKey && this.lastSelection().length) {
      const first = this.lastSelection()[this.lastSelection().length - 1];
      const last = row;

      let startIndex = this.items().findIndex(el => isEqual(el, first));
      let endIndex = this.items().findIndex(el => isEqual(el, last));

      if (endIndex < startIndex) {
        [startIndex, endIndex] = [endIndex, startIndex];
      }

      newSelection = [...this.lastSelection(), ...this.items().slice(startIndex, endIndex + 1)]
        .filter((item, idx, arr) => arr.findIndex(i => isEqual(i, item)) === idx);

      if (this.selectedItems().some(item => isEqual(item, row))) {
        newSelection = [...this.lastSelection()].filter(el => !isEqual(el, row));
      }

    } else {
      const isSelected = this.selectedItems().some(item => isEqual(item, row));
      if (isSelected) {
        newSelection = [...this.lastSelection()].filter(el => !isEqual(el, row));
      } else {
        newSelection = [...this.lastSelection(), row];
      }
    }

    newSelection = [...new Set(newSelection)];

    this.selectedItems.set(newSelection);
    this.lastSelection.set(newSelection);

    this.selectionForm.patchValue({
      masterCheckbox: newSelection.length ? 1 : 0
    });
  }

  public setHoveredItem(item: T): void {
    this.hoveredItem.set(item);
  }

  public getSelectedItems(): T[] {
    return this.selectedItems();
  }

  public getLastSelection(): T[] {
    return this.lastSelection();
  }

  public setAllSelected(): void {
    this.selectedItems.set(this.items());
    this.lastSelection.set(this.items());
    this.masterCheckbox.setValue(2);
  }

  public clearSelection(): void {
    this.selectedItems.set([]);
    this.lastSelection.set([]);
    this.masterCheckbox.setValue(0);
  }

  public clearData(): void {
    this.items.set([]);
    this.selectedItems.set([]);
    this.lastSelection.set([]);
    this.hoveredItem.set(null);
    this.masterCheckbox.setValue(0);
  }

  private compareItems(item1: T, item2: T, key: keyof T): boolean {
    if (!key) return item1 === item2;
    return item1[key] === item2[key];
  }

  public isRowSelected(row: T, key: keyof T): boolean {
    return this.selectedItems().some(item => this.compareItems(item, row, key));
  }

}
